/* eslint-disable no-useless-escape */
/* eslint-disable no-restricted-globals */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useRef } from "react";
import slugify from "slugify";
import { PulseLoader } from "react-spinners";
import { APIContext } from "../../api/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import HelmetLayout from "../../components/HelmetLayout";
import Creatable from "react-select/creatable";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  ArrowLeft,
  Trash2,
  Save,
  Upload,
  Image as ImageIcon,
  Palette,
  ExternalLink,
  RotateCw,
  Sparkles,
} from "lucide-react";

// Canonical theme categories. `value` (slug) must match the browse-tab slugs
// used by the template galleries and the landing site.
const THEME_CATEGORY_OPTIONS = [
  { value: "commerce", label: "E-commerce" },
  { value: "blog", label: "Blog" },
  { value: "directory", label: "Directory" },
  { value: "travel", label: "Travel" },
  { value: "food", label: "Food" },
  { value: "education", label: "Education" },
  { value: "finance", label: "Finance" },
  { value: "health", label: "Health" },
  { value: "tech", label: "Tech" },
  { value: "photography", label: "Photography" },
  { value: "portfolio", label: "Portfolio" },
  { value: "sports", label: "Sports" },
  { value: "news", label: "News" },
  { value: "ngo", label: "NGO" },
  { value: "nft", label: "NFT" },
];

export default function EditTheme({ newTheme }) {
  const navigate = useNavigate();
  const { themeID } = useParams();
  const {
    getTheme,
    deleteTheme,
    createNewTheme,
    updateTheme,
    syncTheme,
    fillThemeWithAI,
  } = useContext(APIContext);

  const [loading, setLoading] = useState(!newTheme);
  const [themeData, setThemeData] = useState({});
  const [themeName, setThemeName] = useState("");
  const [themeDescription, setThemeDescription] = useState("");
  const [themeThumbnail, setThemeThumbnail] = useState("");
  const [themeThumbnailFile, setThemeThumbnailFile] = useState(null);
  const [themeLongThumbnailFile, setThemeLongThumbnailFile] = useState(null);
  const [themeLongThumbnail, setThemeLongThumbnail] = useState("");
  const [isSinglePage, setIsSinglePage] = useState(false);
  const [themeMeta, setThemeMeta] = useState({});
  const [themeOwner, setThemeOwner] = useState("");
  const [themeSlug, setThemeSlug] = useState("");
  const [projectId, setProjectId] = useState("");
  const [isThemeDeleting, setIsThemeDeleting] = useState(false);
  const [isThemeUpdating, setIsThemeUpdating] = useState(false);
  const [isThemeSyncing, setIsThemeSyncing] = useState(false);
  const [isFillWithAILoading, setIsFillWithAILoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const thumbnailRef = useRef(null);
  const thumbnailLongRef = useRef(null);

  useEffect(() => {
    if (!newTheme) {
      getTheme(themeID)
        .then((themedata) => {
          if (themedata) {
            setThemeData(themedata);
            setThemeName(themedata.theme_name || themedata.name || "");
            setThemeDescription(themedata.theme_description || "");
            setThemeThumbnail(themedata.theme_thumbnail || "");
            setThemeLongThumbnail(themedata.theme_long_thumbnail || "");
            setThemeMeta(themedata.theme_meta || {});
            setThemeOwner(themedata.theme_owner || "");
            setThemeSlug(themedata.theme_slug || "");
            setProjectId(themedata.theme_project_id || "");
            setIsSinglePage(Boolean(themedata.theme_is_single_page));
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error("Failed to load theme details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [newTheme, themeID]);

  const handleSyncTheme = async () => {
    if (isThemeSyncing) return;
    setIsThemeSyncing(true);
    try {
      await syncTheme(themeID);
      toast.success("Theme synchronized successfully!");
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to sync theme");
    } finally {
      setIsThemeSyncing(false);
    }
  };

  const handleFillWithAI = async () => {
    if (isFillWithAILoading) return;
    if (!themeName.trim()) {
      toast.warn("Theme name is required for Fill with AI.");
      return;
    }
    if (!themeThumbnail) {
      toast.warn("Upload a thumbnail first so AI can visually inspect the theme.");
      return;
    }

    setIsFillWithAILoading(true);
    try {
      const filled = await fillThemeWithAI(themeName, themeThumbnail, themeMeta);
      setThemeMeta((prev) => {
        const next = { ...(prev || {}) };
        if (filled.keyword != null && (!prev?.keyword || !prev.keyword.length)) {
          next.keyword = Array.isArray(filled.keyword) ? filled.keyword : [];
        }
        if (
          filled.one_line_description != null &&
          !(prev?.one_line_description ?? "").trim()
        ) {
          next.one_line_description = filled.one_line_description;
        }
        if (
          filled.ideal_use_case != null &&
          !(prev?.ideal_use_case ?? "").trim()
        ) {
          next.ideal_use_case = filled.ideal_use_case;
        }
        const prevWhyChoose = prev?.why_choose_answer ?? "";
        const isWhyChooseEmpty =
          !prevWhyChoose.trim() ||
          prevWhyChoose.replace(/<[^>]*>/g, "").trim().length === 0;
        if (filled.why_choose_answer != null && isWhyChooseEmpty) {
          next.why_choose_answer = filled.why_choose_answer;
        }
        if (
          filled.meta_description != null &&
          !(prev?.meta_description ?? "").trim()
        ) {
          next.meta_description = filled.meta_description;
        }
        return next;
      });
      toast.success(
        "AI filled missing fields: keywords, one-liner, use case, and description!"
      );
    } catch (err) {
      const msg =
        err.response?.data?.Output?.message ||
        err.message ||
        "Fill with AI failed.";
      toast.error(msg);
    } finally {
      setIsFillWithAILoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isThemeUpdating) return;

    if (!themeName.trim()) {
      toast.error("Theme name is required.");
      return;
    }

    setIsThemeUpdating(true);
    try {
      if (newTheme) {
        await createNewTheme(
          themeName,
          themeDescription,
          themeThumbnailFile,
          themeLongThumbnailFile,
          isSinglePage,
          themeMeta,
          themeOwner,
          themeSlug,
          projectId
        );
        toast.success("Theme created successfully!");
        navigate("/themes");
      } else {
        await updateTheme(
          themeID,
          themeName,
          themeDescription,
          themeThumbnailFile,
          themeLongThumbnailFile,
          isSinglePage,
          themeMeta,
          themeOwner
        );
        toast.success("Theme updated successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to save theme");
    } finally {
      setIsThemeUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (isThemeDeleting) return;
    setIsThemeDeleting(true);
    try {
      await deleteTheme(themeID);
      toast.success("Theme deleted successfully!");
      navigate("/themes");
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to delete theme");
      setIsThemeDeleting(false);
    }
  };

  return (
    <>
      <HelmetLayout
        title={newTheme ? "New Theme" : `${themeName || "Edit"} - Theme`}
      />

      <div className="space-y-6 pb-16 max-w-4xl">
        {/* Navigation & Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link
              to="/themes"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mb-2"
            >
              <ArrowLeft size={14} />
              Back to Themes
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Palette className="text-[#623ce7]" size={24} />
              {newTheme ? "Create New Theme" : `Edit Theme: ${themeName}`}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {newTheme
                ? "Deploy a customer-facing site theme with thumbnails, tags, and category mappings."
                : `Manage metadata, screenshots, and AI descriptions for theme ${themeID}.`}
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {!newTheme && themeSlug && (
              <a
                href={`https://${themeSlug}.grigora.app`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
              >
                <ExternalLink size={14} />
                Open Live
              </a>
            )}

            {!newTheme && (
              <button
                type="button"
                onClick={handleSyncTheme}
                disabled={isThemeSyncing}
                className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <RotateCw
                  size={14}
                  className={isThemeSyncing ? "animate-spin" : ""}
                />
                {isThemeSyncing ? "Syncing..." : "Sync"}
              </button>
            )}

            {!newTheme && (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                className="px-3.5 py-2 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={isThemeUpdating}
              className="px-4 py-2 text-xs font-semibold text-white bg-[#623ce7] hover:bg-[#5030bc] rounded-lg transition-colors shadow-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              {isThemeUpdating ? (
                <PulseLoader size={6} color="#ffffff" />
              ) : (
                <>
                  <Save size={14} />
                  {newTheme ? "Create Theme" : "Save Changes"}
                </>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center shadow-sm">
            <PulseLoader size={10} color="#623ce7" />
            <p className="text-xs text-slate-400 mt-3">Loading theme...</p>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            {/* General Info Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
              <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
                Theme Identity & Routing
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Theme Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Apex Modern Business"
                    value={themeName}
                    onChange={(e) => setThemeName(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
                  />
                </div>

                {/* Theme Slug */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Theme Slug
                  </label>
                  <input
                    type="text"
                    readOnly={!newTheme}
                    placeholder="e.g. apex-business"
                    value={themeSlug}
                    onChange={(e) => setThemeSlug(e.target.value)}
                    className={`w-full px-3.5 py-2.5 rounded-lg text-sm transition-all ${
                      !newTheme
                        ? "bg-slate-100 text-slate-500 border border-slate-200 cursor-not-allowed font-mono text-xs"
                        : "bg-slate-50 border border-slate-200 text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none"
                    }`}
                  />
                  {!newTheme && (
                    <p className="text-[11px] text-slate-400 mt-1">
                      Theme slug cannot be modified after initial creation.
                    </p>
                  )}
                </div>

                {/* Project ID */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Source Project ID
                  </label>
                  <input
                    type="text"
                    readOnly={!newTheme}
                    placeholder="e.g. project-abc123xyz"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className={`w-full px-3.5 py-2.5 rounded-lg text-sm transition-all ${
                      !newTheme
                        ? "bg-slate-100 text-slate-500 border border-slate-200 cursor-not-allowed font-mono text-xs"
                        : "bg-slate-50 border border-slate-200 text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none"
                    }`}
                  />
                </div>

                {/* Owner */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Theme Owner / Creator
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Grigora Team"
                    value={themeOwner}
                    onChange={(e) => setThemeOwner(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
                  />
                </div>

                {/* Single Page Switch */}
                <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <span className="block text-xs font-semibold text-slate-800">
                      Single Page Website
                    </span>
                    <span className="text-[11px] text-slate-400">
                      Does this theme represent a single-page landing site?
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsSinglePage((prev) => !prev)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      isSinglePage ? "bg-[#623ce7]" : "bg-slate-200"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        isSinglePage ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Categories */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Template Categories
                </label>
                <Creatable
                  isMulti
                  options={THEME_CATEGORY_OPTIONS}
                  value={themeMeta?.category || []}
                  onChange={(selectedOptions) => {
                    setThemeMeta({
                      ...(themeMeta || {}),
                      category: selectedOptions,
                    });
                  }}
                  onCreateOption={(inputValue) => {
                    const slug = slugify(inputValue, {
                      trim: false,
                      strict: true,
                      lower: true,
                    });
                    const newOption = { value: slug, label: inputValue };
                    setThemeMeta({
                      ...(themeMeta || {}),
                      category: [...(themeMeta?.category || []), newOption],
                    });
                  }}
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "#f8fafc",
                      borderColor: "#e2e8f0",
                      borderRadius: "0.5rem",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#cbd5e1",
                      },
                    }),
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Full Theme Description
                </label>
                <textarea
                  rows={4}
                  placeholder="Detailed overview of this theme's layout, features, and target industry..."
                  value={themeDescription}
                  onChange={(e) => setThemeDescription(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all resize-y"
                />
              </div>
            </div>

            {/* AI Copywriting & Marketing Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                <div>
                  <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Sparkles className="text-purple-600" size={18} />
                    AI Marketing & Copywriting
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Generate rich descriptions, SEO tags, and use cases automatically.
                  </p>
                </div>

                <button
                  type="button"
                  disabled={
                    isFillWithAILoading ||
                    !themeName.trim() ||
                    !themeThumbnail
                  }
                  onClick={handleFillWithAI}
                  className="px-3.5 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shrink-0"
                >
                  <Sparkles
                    size={14}
                    className={isFillWithAILoading ? "animate-spin" : ""}
                  />
                  {isFillWithAILoading ? "Analyzing..." : "Fill with AI"}
                </button>
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Keywords & Search Tags
                </label>
                <Creatable
                  isMulti
                  placeholder="Type keyword and press Enter..."
                  options={[]}
                  value={themeMeta?.keyword || []}
                  onChange={(selectedOptions) => {
                    setThemeMeta({
                      ...(themeMeta || {}),
                      keyword: selectedOptions,
                    });
                  }}
                  onCreateOption={(inputValue) => {
                    const slug = slugify(inputValue, {
                      trim: false,
                      strict: true,
                      lower: true,
                    });
                    const newOption = { value: slug, label: inputValue };
                    setThemeMeta({
                      ...(themeMeta || {}),
                      keyword: [...(themeMeta?.keyword || []), newOption],
                    });
                  }}
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "#f8fafc",
                      borderColor: "#e2e8f0",
                      borderRadius: "0.5rem",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#cbd5e1",
                      },
                    }),
                  }}
                />
              </div>

              {/* One Line Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  One-Line Description
                </label>
                <input
                  type="text"
                  placeholder="Short tagline for the template (e.g. A clean, typography-led aesthetic for modern SaaS startups.)"
                  value={themeMeta?.one_line_description ?? ""}
                  onChange={(e) => {
                    setThemeMeta({
                      ...(themeMeta || {}),
                      one_line_description: e.target.value,
                    });
                  }}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
                />
              </div>

              {/* Ideal Use Case */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Ideal Use Case
                </label>
                <textarea
                  rows={3}
                  placeholder="Who is this template best for? (e.g. Software companies looking to build trust quickly with a serious, performance-oriented look.)"
                  value={themeMeta?.ideal_use_case ?? ""}
                  onChange={(e) => {
                    setThemeMeta({
                      ...(themeMeta || {}),
                      ideal_use_case: e.target.value,
                    });
                  }}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all resize-y"
                />
              </div>

              {/* Why Choose Answer (Rich Text) */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Why Choose This Theme (Rich Text)
                </label>
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden [&_.ql-toolbar]:bg-slate-50 [&_.ql-toolbar]:border-none [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-200 [&_.ql-container]:border-none [&_.ql-editor]:min-h-[160px] [&_.ql-editor]:text-sm">
                  <ReactQuill
                    theme="snow"
                    value={themeMeta?.why_choose_answer ?? ""}
                    onChange={(value) => {
                      setThemeMeta({
                        ...(themeMeta || {}),
                        why_choose_answer: value,
                      });
                    }}
                    placeholder="Describe why customers should choose this theme..."
                    modules={{
                      toolbar: [
                        [{ header: [2, 3, false] }],
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link", "clean"],
                      ],
                    }}
                  />
                </div>
              </div>

              {/* Meta Description */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    SEO Meta Description
                  </label>
                  <span
                    className={`text-[11px] ${
                      (themeMeta?.meta_description || "").length > 160
                        ? "text-rose-500 font-semibold"
                        : "text-slate-400"
                    }`}
                  >
                    {(themeMeta?.meta_description || "").length} / 160 characters
                  </span>
                </div>
                <textarea
                  rows={2}
                  placeholder="Concise SEO search snippet (120-160 characters)..."
                  value={themeMeta?.meta_description || ""}
                  onChange={(e) => {
                    setThemeMeta({
                      ...(themeMeta || {}),
                      meta_description: e.target.value,
                    });
                  }}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all resize-y"
                />
              </div>
            </div>

            {/* Visual Assets Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
              <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
                Thumbnails & Visuals
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Standard Thumbnail */}
                <div className="space-y-3">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Standard Thumbnail (Square / 16:9)
                  </label>

                  <div className="flex flex-col items-start gap-3">
                    {themeThumbnail ? (
                      <div className="relative w-full h-44 rounded-lg border border-slate-200 overflow-hidden bg-slate-100 shadow-sm">
                        <img
                          src={themeThumbnail}
                          alt="Theme thumbnail"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-44 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                        <ImageIcon size={28} className="mb-1 text-slate-300" />
                        <span className="text-xs">No thumbnail uploaded</span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => thumbnailRef.current?.click()}
                      className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                    >
                      <Upload size={14} />
                      {themeThumbnail ? "Replace Thumbnail" : "Upload Thumbnail"}
                    </button>

                    <input
                      ref={thumbnailRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.readAsDataURL(file);
                          reader.onloadend = () => {
                            setThemeThumbnail(reader.result);
                          };
                          setThemeThumbnailFile(file);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Long Thumbnail */}
                <div className="space-y-3">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Full-Length Screenshot (Long Thumbnail)
                  </label>

                  <div className="flex flex-col items-start gap-3">
                    {themeLongThumbnail ? (
                      <div className="relative w-full h-44 rounded-lg border border-slate-200 overflow-hidden bg-slate-100 shadow-sm">
                        <img
                          src={themeLongThumbnail}
                          alt="Theme long thumbnail"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-44 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                        <ImageIcon size={28} className="mb-1 text-slate-300" />
                        <span className="text-xs">No long thumbnail</span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => thumbnailLongRef.current?.click()}
                      className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                    >
                      <Upload size={14} />
                      {themeLongThumbnail
                        ? "Replace Long Image"
                        : "Upload Long Screenshot"}
                    </button>

                    <input
                      ref={thumbnailLongRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.readAsDataURL(file);
                          reader.onloadend = () => {
                            setThemeLongThumbnail(reader.result);
                          };
                          setThemeLongThumbnailFile(file);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Link
                to="/themes"
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isThemeUpdating}
                className="px-5 py-2.5 text-xs font-semibold text-white bg-[#623ce7] hover:bg-[#5030bc] rounded-lg transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                {isThemeUpdating ? (
                  <PulseLoader size={6} color="#ffffff" />
                ) : (
                  <>
                    <Save size={14} />
                    {newTheme ? "Create Theme" : "Save Changes"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Custom Confirmation Modal for Deletion */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-xl border border-slate-200 shadow-2xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-lg font-bold text-slate-900">Delete Theme</h3>
              <p className="text-sm text-slate-600">
                Are you sure you want to permanently delete{" "}
                <strong className="text-slate-900 font-semibold">
                  {themeName || themeID}
                </strong>
                ? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isThemeDeleting}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isThemeDeleting}
                  className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
                >
                  {isThemeDeleting ? (
                    <PulseLoader size={6} color="#ffffff" />
                  ) : (
                    <>
                      <Trash2 size={14} />
                      Confirm Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
